import { showNotification } from "context/notification.service";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TeamInfos } from "types";
import { User } from "types/user";
import { useDataStore } from "@store/user_org";
import { role_dict } from "@screens/sampleData";

const defaultColors = [
  "bg-indigo-400",
  "bg-violet-400",
  "bg-blue-400",
  "bg-teal-400",
  "bg-purple-400",
  "bg-lime-400",
  "bg-green-400",
  "bg-sky-400",
];

export function getUserColor(user: User, index: number): string {
  return (
    user.customization?.color_tag || defaultColors[index % defaultColors.length]
  );
}

export function getUserDisplayName(user: User): string {
  return user.infos?.display_name || user.infos?.full_name || "Utilisateur";
}

export type MemberUser = {
  id: string;
  name: string;
  role: string;
  color: string;
};

export function orgMembersToUsers(members: User[]): MemberUser[] {
  return members.map((m, i) => ({
    id: m.id,
    name: getUserDisplayName(m),
    role: m.role || "",
    color: getUserColor(m, i),
  }));
}

const teamRoles: { key: keyof TeamInfos; label: string }[] = [
  { key: "receptionniste_id", label: "Réceptionniste" },
  { key: "chef_atelier_id", label: "Chef d'atelier" },
  { key: "mecanicien_id", label: "Mécanicien" },
  { key: "carrossier_id", label: "Carrossier" },
  { key: "comptabilite_id", label: "Comptabilité" },
  // Legacy construction-template roles (unused in garage context)
  // { key: "commercial_id", label: "Commercial" },
  // { key: "conductor_id", label: "Conducteur" },
  // { key: "sav_id", label: "SAV" },
  // { key: "communication_id", label: "Communication" },
];

export const MemberAssignmentBlock = ({
  team,
  onTeamChange,
}: {
  team?: TeamInfos;
  onTeamChange?: (team: TeamInfos) => void;
}) => {
  const orgMembers = useDataStore((state) => state.orgMembers);
  const users = orgMembersToUsers(
    orgMembers.filter((m) => m.config?.access_type !== "GUEST")
  );

  const currentTeam: TeamInfos = team || {};

  const handleAssignMemberToRole = (userId: string, roleKey: string) => {
    if (!onTeamChange) return;
    onTeamChange({
      ...currentTeam,
      [roleKey]: userId || undefined,
    });
  };

  const assignedRoles = teamRoles.filter(
    (r) => currentTeam[r.key as keyof TeamInfos],
  );

  return (
    <div className="flex flex-col gap-1 ">
      {assignedRoles.length === 0 && users.length > 0 && (
        <div className="text-h3 text-neutral-400">Aucun membre assigné</div>
      )}
      {assignedRoles.map((role) => {
        const userId = currentTeam[role.key as keyof TeamInfos];
        const user = users.find((u) => u.id === userId);
        if (!user) return null;
        return (
          <div key={role.key}>
            <MemberAssignmentItem
              assignMemberToRole={(selectedUserId) => {
                handleAssignMemberToRole(selectedUserId, role.key);
              }}
              users={users}
              currentUserId={userId}
              label={role.label}
              memberName={user.name}
              memberColor={user.color}
            />
          </div>
        );
      })}
      {teamRoles
        .filter((r) => !currentTeam[r.key as keyof TeamInfos])
        .map((role) => (
          <div key={role.key}>
            <MemberAssignmentItem
              assignMemberToRole={(selectedUserId) => {
                handleAssignMemberToRole(selectedUserId, role.key);
              }}
              users={users}
              label={role.label}
              memberName="Ajouter"
              unassigned
            />
          </div>
        ))}
    </div>
  );
};

export const MemberItem = ({
  memberName,
  memberColor,
  onClick,
}: {
  memberName: string;
  memberColor: string;
  onClick?: () => void;
}) => {
  return (
    <div className="flex flex-row gap-4 text-h2 items-center">
      <div
        onClick={() => onClick && onClick()}
        style={{
          backgroundColor: memberColor,
        }}
        className={` w-fit rounded-full text-white px-4 py-1 hover:ring-4 hover:ring-neutral-300 cursor-pointer`}
      >
        {memberName}
      </div>
    </div>
  );
};

export const MemberAssignmentItem = ({
  label,
  memberName,
  memberColor,
  assignMemberToRole,
  users,
  currentUserId,
  unassigned,
}: {
  label?: string;
  memberName: string;
  memberColor?: string;
  assignMemberToRole: (userId: string) => void;
  users?: MemberUser[];
  currentUserId?: string;
  unassigned?: boolean;
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Update position when opening, clamped to viewport
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // After a tick, measure the menu and clamp to viewport
      requestAnimationFrame(() => {
        const menuWidth = menuRef.current?.offsetWidth || 300;
        const maxLeft = window.innerWidth - menuWidth - 16;
        setPosition({
          top: rect.bottom + 8,
          left: Math.max(8, Math.min(rect.left, maxLeft)),
        });
      });
    }
  }, [open]);

  // Close on click outside or scroll
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    const handleScroll = () => setOpen(false);

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setVisible(true);
    } else {
      const timeout = setTimeout(() => setVisible(false), 200);
      return () => clearTimeout(timeout);
    }
  }, [open]);

  return (
    <>
      <div className="flex flex-row gap-4 text-h2 items-center">
        {label && <div className="text-neutral-400 w-150">{label}</div>}
        <div
          ref={buttonRef}
          onClick={() => {
            setOpen(!open);
          }}
          style={unassigned ? undefined : { backgroundColor: memberColor }}
          className={`w-fit rounded-full px-4 py-1 hover:ring-4 hover:ring-neutral-300 cursor-pointer ${
            unassigned
              ? "text-neutral-400 border border-neutral-300 border-dashed"
              : "text-white"
          }`}
        >
          {memberName}
        </div>
      </div>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{
              position: "fixed",
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
            className={`
              ${visible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95"}
              z-[9999] transition-all duration-200 flex flex-col text-shadow_50 border-1 border-shadow_10 bg-white shadow-xl rounded-xl`}
          >
            <MemberAssignmentModal
              users={users || []}
              currentUserId={currentUserId}
              assignMember={(userid) => {
                assignMemberToRole(userid);
                showNotification({
                  type: "success",
                  message: `Membre assigné !`,
                });
                setOpen(false);
              }}
            />
          </div>,
          document.body,
        )}
    </>
  );
};

export const MemberAssignmentModal = ({
  assignMember,
  users,
  currentUserId,
}: {
  assignMember: (userId: string) => void;
  users: MemberUser[];
  currentUserId?: string;
}) => {
  const grouped = users.reduce<Record<string, MemberUser[]>>((acc, user) => {
    const key = user.role || "Autre";
    if (!acc[key]) acc[key] = [];
    acc[key].push(user);
    return acc;
  }, {});
  const groups = Object.entries(grouped);

  return (
    <div className="flex flex-row text-h2 p-2 gap-4">
      <div className="flex flex-col gap-1">
        <div className="text-h3 text-neutral-400 px-1 whitespace-nowrap">—</div>
        <div
          onClick={() => assignMember("")}
          className={`${!currentUserId ? "ring-2 ring-black " : ""}w-fit rounded-full bg-neutral-300 hover:ring-4 hover:ring-neutral-200 cursor-pointer text-white px-4 py-1`}
        >
          Aucun
        </div>
      </div>
      {users.length === 0 && (
        <div className="text-neutral-400 text-h3 p-2">Aucun membre</div>
      )}
      {groups.map(([role, members]) => (
        <div key={role} className="flex flex-col gap-1">
          <div className="text-h3 text-neutral-400 px-1 whitespace-nowrap">{role_dict[role as keyof typeof role_dict] || role}</div>
          {members.map((user) => (
            <div
              key={user.id}
              onClick={() => assignMember(user.id)}
              style={{ backgroundColor: user.color }}
              className={`${currentUserId === user.id ? "ring-2 ring-black " : ""}w-fit rounded-full hover:ring-4 hover:ring-neutral-300 cursor-pointer text-white px-4 py-1`}
            >
              {user.name}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
